import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { SalesTeamRow } from '../../sales-team-management.model';
import {
  initialsOf,
  missingManagerLabel,
  SALES_TEAM_LEVEL_BADGE,
  salesTeamLevel,
} from '../../sales-team-management.util';

/**
 * One person in the hierarchy tree. The whole row toggles its reports; the trailing actions slot
 * (ng-content) is where "Assign manager" / "Change manager" goes once reporting lines are editable.
 */
@Component({
  selector: 'app-sales-team-row',
  standalone: false,
  templateUrl: './sales-team-row.component.html',
  styleUrl: './sales-team-row.component.scss',
})
export class SalesTeamRowComponent implements OnChanges {
  @Input({ required: true }) row!: SalesTeamRow;
  /** Select mode adds the checkbox column; only `selectable` rows get a checkbox in it. */
  @Input() selectMode: boolean = false;
  /** False for RSMs (they cannot be given a manager) and for other levels once someone is ticked. */
  @Input() selectable: boolean = false;
  @Input() selected: boolean = false;

  /** Expand or collapse, from the chevron. */
  @Output() toggle: EventEmitter<string> = new EventEmitter<string>();
  /** Focus this person's branch, from a click anywhere else on the row. */
  @Output() select: EventEmitter<string> = new EventEmitter<string>();
  @Output() selectedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  protected hasReports: boolean = false;
  protected initials: string = '';
  protected levelClass: string = 'level-other';
  protected badgeClass: string = 'badge-soft-secondary';
  protected shortLabel: string = '';
  protected noManagerLabel: string = '';
  /** CSS colours for the focused-path segments, from the row's colour keys. */
  protected pathColors: { guides: string[]; line: string; across: string; stem: string } | null = null;

  ngOnChanges(): void {
    const node = this.row.node;
    const level = salesTeamLevel(node.designation);
    const badge = level ? SALES_TEAM_LEVEL_BADGE[level] : null;

    this.hasReports = node.reports.length > 0;
    this.initials = initialsOf(node.name);
    this.levelClass = `level-${level ?? 'other'}`;
    this.badgeClass = badge?.badgeClass ?? 'badge-soft-secondary';
    this.shortLabel = badge?.shortLabel ?? node.designation;
    this.noManagerLabel = missingManagerLabel(node.designation);

    const tones = this.row.path?.tones;
    const color = (tone: string): string => (tone ? `var(--tone-${tone})` : '');

    this.pathColors = tones
      ? {
          guides: tones.guides.map(color),
          line: color(tones.line),
          across: color(tones.across),
          stem: color(tones.stem),
        }
      : null;
  }

  protected onCheckboxChange(event: Event): void {
    this.selectedChange.emit((event.target as HTMLInputElement).checked);
  }

  protected onRowClick(): void {
    this.select.emit(this.row.node.id);
  }

  /** The chevron only expands or collapses; it must not also change the focus. */
  protected onToggleClick(event: MouseEvent): void {
    event.stopPropagation();
    this.toggle.emit(this.row.node.id);
  }
}
